import { createContext, useEffect, useState, useRef, useContext } from "react";
import { HubConnectionBuilder } from '@microsoft/signalr';
import { Link, useHistory } from 'react-router-dom';

const AppDataContext = createContext(null);

const AppDataContextProvider = ({ children }) => {

    // SignalR event processing
    const hubConnection = useRef(null);
    const [hubConnected, setHubConnected] = useState(false);

    // user information context
    const [isLoggedIn, setIsLoggedIn] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [fullName, setFullName] = useState(null);
    const [initials, setInitials] = useState(null);
    const [hasProfile, setHasProfile] = useState(null);
    const [hasPayment, setHasPayment] = useState(null);
    const [last4, setLast4] = useState(null);
    const [brand, setBrand] = useState(null);
    const [expiredDate, setExpiredDate] = useState(null);
    const [taxExemptTaxDocSecureId, setTaxExemptTaxDocSecureId] = useState(null);
    const [emailVerified, setEmailVerified] = useState(null);

    // alerts get set asynchrnously, so need to have a ref assigned
    const [alerts, _setAlerts] = useState(null);
    const alertsRef = useRef(null);
    const setAlerts = (alerts) => {
        _setAlerts(alerts);
        alertsRef.current = alerts;
    }

    // bidding context
    const [bidUniqueIds, setBidUniqueIds] = useState([]);
    const [registeredAuctionsChanged, setRegisteredAuctionsChanged] = useState(null);
    const registeredAuctions = useRef([]);
    const setRegisteredAuction = (auctionId, paddleNumber) => {
        // ignore if called with existing knowledge
        if (auctionId in registeredAuctions.current &&
            registeredAuctions.current[auctionId].paddleNumber === paddleNumber) {
            return;
        }
        console.log("  Registered for Auction " + auctionId + ", paddle number " + (paddleNumber == null ? " Not set" : paddleNumber));
        registeredAuctions.current[auctionId] = {
            paddleNumber: paddleNumber
        };
        setRegisteredAuctionsChanged(Math.random());
    };
    const isRegisteredForAuction = (auctionId) => {
        return auctionId in registeredAuctions.current;
    };

    // software version context
    const [softwareVersion, setSoftwareVersion] = useState(null);
    const [newVersion, setNewVersion] = useState(false);
    const testSoftwareVersion = (currentSoftwareVersion) => {
        if (currentSoftwareVersion) {
            if (softwareVersion != null && softwareVersion != currentSoftwareVersion)
                setNewVersion(true);
            setSoftwareVersion(currentSoftwareVersion);
        }
    }

    const setPayment = (paymentInfo) => {
        if (paymentInfo == null) {
            setHasPayment(null);
            setLast4(null);
            setBrand(null);
            setExpiredDate(null);
        } else if (paymentInfo.hasPayment !== true) {
            setHasPayment(false);
            setLast4(null);
            setBrand(null);
            setExpiredDate(null);
        } else {
            setHasPayment(true);
            setLast4(paymentInfo.last4);
            setBrand(paymentInfo.brand);
            setExpiredDate(new Date(paymentInfo.expiredDate));
        }
    }

    const loggedIn = (loginResponse) => {
        console.log("AppDataContext.loggedIn() Called for session id " + loginResponse.sessionId + ", user " + loginResponse.firstName + ' ' + loginResponse.lastName);
        setFullName(loginResponse.firstName + ' ' + loginResponse.lastName);
        setInitials(loginResponse.firstName.substring(0, 1).toUpperCase() + loginResponse.lastName.substring(0, 1).toUpperCase());
        setHasProfile(loginResponse.hasProfile);

        setPayment(loginResponse);

        setEmailVerified(loginResponse.emailVerified);
        setAlerts(loginResponse.alerts);
        setTaxExemptTaxDocSecureId(loginResponse.taxExemptTaxDocSecureId);

        loginResponse.currentRegisteredAuctions?.forEach((reg, i) => {
            setRegisteredAuction(reg.secureAuctionId, reg.paddleNumber);
        });

        if (hubConnected) {
            alertArrivedRef.current = alertArrived;
            subscribeToAlerts(loginResponse.sessionId, alertArrivedRef.current);
        }
        else
            console.log("Hub not up yet, subscribing to alerts later");

        setSessionId(loginResponse.sessionId);
        setIsLoggedIn(true);
    };


    useEffect(() => {
        console.log("Hub Connected: " + (hubConnected ? "true" : "false"));
        if (hubConnected && alertArrivedRef.current == null) {
            alertArrivedRef.current = alertArrived;
            if (sessionId)
                subscribeToAlerts(sessionId, alertArrivedRef.current);
        }
        if (hubConnected == false && alertArrivedRef.current != null) {
            if (sessionId)
                unsubscribeToAlerts(sessionId, alertArrivedRef.current);
            alertArrivedRef.current = null;
        }
    }, [hubConnected]);


    const alertArrivedRef = useRef(null);
    const alertArrived = (alertEvent) => {
        const alertMessage = alertEvent.detail;
        let newAlerts = null;
        if (alertsRef != null)
            newAlerts = [alertMessage, ...alertsRef.current];
        else
            newAlerts = [alertMessage];

        setAlerts(newAlerts);

        // if the alert is that you have been approved for an auction, tell the context
        // so that it can update the list.  this should trickle down to any auctions the user is looking at
        // by re-selecting the auction/lots on the page
        if (alertMessage.alertEnumName == "BidderUserAuctionApproved")
            setRegisteredAuction(alertMessage.secureAuctionId, alertMessage.paddleNumber);
    }

    const loggedOut = () => {
        if (alertArrivedRef.current != null) {
            unsubscribeToAlerts(sessionId, alertArrivedRef.current);
            alertArrivedRef.current = null;
        }

        setIsLoggedIn(false);
        setSessionId(null);
        setFullName(null);
        setInitials(null);
        setHasProfile(null);
        setPayment(null);
        setEmailVerified(null);
        setBidUniqueIds([]);
        registeredAuctions.current = [];
        setRegisteredAuctionsChanged(Math.random());
        setAlerts(null);

    };


    const connectToSignalR = () => {
        console.log('Connecting to SignalR Hub');

        // construct a connection to the Bid Hub
        hubConnection.current = new HubConnectionBuilder()
            .withUrl('/api/hubs/bid')
            .withAutomaticReconnect()
            .build();

        if (hubConnection.current) {
            hubConnection.current.logging = true;
            hubConnection.current.start()
            .then(res => {
                console.log('Connected to SignalR Hub: connectionId=' + hubConnection.current.connectionId);
                setHubConnected(true);

                hubConnection.current.on('ReceiveBid', message => {
                    console.log('Bid Received');
                    // make sure it's not the broadcast from this user's bid
                    const existingBidIx = bidUniqueIds.indexOf(message.bidUniqueId);
                    if (existingBidIx > -1) {
                        // okay, it came through, we can remove it from the array
                        bidUniqueIds.splice(existingBidIx, 1);
                    } else {
                        publishNewBid(message);
                    }
                });

                hubConnection.current.on('Alert', message => {
                    console.log('Alert Received');
                    publishNewAlert(message);
                });
            })
            .catch(e => {
                console.log('Connection failed: ', e);
            });

            hubConnection.current.onreconnecting(error => {
                console.log("SignalR reconnecting attempt: error = " + error);
                setHubConnected(false);
            });

            hubConnection.current.onreconnected(connectionId => {
                console.log("SignalR reconnected: connectionId = " + connectionId);
                setHubConnected(true);
            });

            hubConnection.current.onclose(error => {
                console.log("SignalR disconnected: error = " + error);
                setHubConnected(false);
            });
        }
    };


    const bidEventName = "NewBid";
    const alertEventName = "NewAlert";

    const subscribeToBids = (groupName, listener) => {
        console.log('Monitoring Bids for ' + groupName);

        // start monitoring Alerts
        // Join a SignalR group based on the users session ID. MonitorAuction is C# method name in the hub
        hubConnection.current.invoke("MonitorAuction", groupName)
            .catch(err => {
                console.log(err);
            });

        document.addEventListener(bidEventName, listener);
    }

    const unsubscribeToBids = (groupName, listener) => {
        console.log('Removing Bids Monitor for ' + groupName);

        // Stop monitoring Alerts
        // Join a SignalR group based on the users session ID. MonitorAuction is C# method name in the hub
        hubConnection.current.invoke("StopMonitorAuction", groupName)
            .catch(err => {
                console.log(err);
            });

        document.removeEventListener(bidEventName, listener);
    }

    const publishNewBid = (bid) => {
        bid.isBroadcast = true;
        const event = new CustomEvent(bidEventName, {
            detail: bid
        });
        document.dispatchEvent(event);
    };

    const subscribeToAlerts = (groupName, listener) => {
        console.log('Monitoring Alerts for ' + groupName);

        // start monitoring Alerts
        // Join a SignalR group based on the users session ID. MonitorAuction is C# method name in the hub
        hubConnection.current.invoke("MonitorAuction", groupName)
            .catch(err => {
                console.log(err);
            });

        document.addEventListener(alertEventName, listener);
    }

    const unsubscribeToAlerts = (groupName, listener) => {
        console.log('Removing Alerts Monitor for ' + groupName);

        // start monitoring Alerts
        // Join a SignalR group based on the users session ID. MonitorAuction is C# method name in the hub
        hubConnection.current.invoke("StopMonitorAuction", groupName)
            .catch(err => {
                console.log(err);
            });

        document.removeEventListener(alertEventName, listener);
    }

    const publishNewAlert = (alertMessage) => {
        const event = new CustomEvent(alertEventName, {
            detail: alertMessage
        });
        document.dispatchEvent(event);
    };

    // network connectivity detection, re-establish monitors
    // currently via a reload of the page.
    const [reloadPage, setReloadPage] = useState(false);
    useEffect(() => {
        if (reloadPage) {
            console.log("Reloading screen");
            window.location.reload();
            setReloadPage(false);
        }
    }, [reloadPage]);

    const [status, setStatus] = useState(() => {
        if (navigator.onLine) {
            console.log("Connected to network.");
            return true;
        } else {
            return false;
        }
    });
    useEffect(() => {
        console.log("Attaching online and offline events.");

        window.ononline = (e) => {
            console.log("Network connection just occurred.");
            const networkRecoveredDelay = 2 * 1000;
            setTimeout(() => {
                setStatus(true);
                setReloadPage(true);
            }, networkRecoveredDelay);
        };
        window.onoffline = (e) => {
            console.log("Network connection lost.");
            setStatus(false);
        };
    }, [status]);

    const sleepCheckTimer = useRef(null);
    const lastSleepCheckTime = useRef(null);

    const sleepCheck = () => {
        const sleepCheckTimerDelay = 10 * 1000;
        sleepCheckTimer.current = setTimeout(() => {
            const currentTime = (new Date()).getTime();
            if (lastSleepCheckTime.current &&
                currentTime > (lastSleepCheckTime.current + sleepCheckTimerDelay * 2)) {  // ignore small delays
                console.log("It looks like we slept a bit.  reloading the page.")
                setReloadPage(true);
            }
            lastSleepCheckTime.current = currentTime;
            sleepCheck();
        }, sleepCheckTimerDelay);
    }

    useEffect(() => {
        connectToSignalR();
        ///
        /// take this out for now in favor of monitoring connections via the signalR connection events
        ///
        //sleepCheck();

        return () => {
            if (alertArrivedRef.current != null) {
                unsubscribeToAlerts(sessionId, alertArrivedRef.current);
                alertArrivedRef.current = null;
            }

            // close the connection
            if (hubConnection.current) {
                console.log('Disonnecting from hub');
                hubConnection.current.stop();
            }

            // remove timer
            if (sleepCheckTimer.current) {
                console.log('clearing sleep check timer');
                clearTimeout(sleepCheckTimer.current);
                sleepCheckTimer.current = null;
                lastSleepCheckTime.current = null;
            }
        };
    }, [])

    let model = {
        isLoggedIn: isLoggedIn,
        sessionId: sessionId,
        fullName: fullName,
        initials: initials,
        hasProfile: hasProfile,
        hasPayment: hasPayment,
        last4: last4,
        brand: brand,
        expiredDate: expiredDate,
        emailVerified: emailVerified,
        taxExemptTaxDocSecureId: taxExemptTaxDocSecureId,
        alerts: alerts,

        loggedIn: loggedIn,
        loggedOut: loggedOut,
        setHasProfile: setHasProfile,
        setPayment: setPayment,
        setEmailVerified: setEmailVerified,
        setAlerts: setAlerts,
        setTaxExemptTaxDocSecureId: setTaxExemptTaxDocSecureId,

        bidUniqueIds: bidUniqueIds,
        registeredAuctionsChanged: registeredAuctionsChanged,
        setRegisteredAuction: setRegisteredAuction,
        isRegisteredForAuction: isRegisteredForAuction,

        softwareVersion: softwareVersion,
        newVersion: newVersion,
        testSoftwareVersion: testSoftwareVersion,

        hubConnected: hubConnected,
        subscribeToBids: subscribeToBids,
        unsubscribeToBids: unsubscribeToBids,
        subscribeToAlerts: subscribeToAlerts,
        unsubscribeToAlerts: unsubscribeToAlerts,
    };

    return (
        <AppDataContext.Provider value={model}>
            {children}
        </AppDataContext.Provider>
    );
};

const VLink = ({ to, onClick, target, ...otherProps }) => {
    const context = useContext(AppDataContext);
    const history = useHistory();

    // intercept the click event and test if we've updated the version recently
    const testAndLink = (e) => {
        var onClickResult = null;
        if (onClick)
            onClickResult = onClick();

        // returning false from onClick will cancel the navigate
        if (onClickResult === false)
            return;

        // otherwise, they could be returning a state variable to be passed on to the history event.
        var navState = null;

        // override with the click result, if set
        if (onClickResult)
            navState = onClickResult;

        // determine if they are using a to object or just a string for the link href
        const location = to.pathname ? to.pathname : to;

        if (context.newVersion) {
            console.log("Front end new version detected, refreshing on link");
            if (target) {
                window.open(location, target);
                window.location.reload();
            } else {
                window.location = location;
            }
        }
        else {
            // when a state is returned from the OnClick, we want to honor it by going directly to the history
            if (navState) {
                e.preventDefault();
                // standard react navigate.  set the history
                history.push(location, navState);
            }
            // otherwise we fall through (no prevent default) and let <Link> do all the work
        }
    };

    return (
        <Link to={to} target={target} onClick={testAndLink} {...otherProps} />
    );
}

export { AppDataContext, AppDataContextProvider, VLink };
